
import { useState, useEffect, useContext } from "react";
import {
    DatePicker,
    TimePicker,
    Radio,
    Checkbox,
    Button,
    Card,
    Select,
    message,
    Space,
    Typography,
    Divider,
    Row,
    Col,
    Input,
} from "antd";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export default function MakeSpecialPermission() {
    const [selectedDate, setSelectedDate] = useState(null);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [gender, setGender] = useState(null);
    const [officeType, setOfficeType] = useState([]);
    const [reason, setReason] = useState("");

    const [submitting, setSubmitting] = useState(false);

    // Head Office
    const [headOfficeSelection, setHeadOfficeSelection] = useState(null);
    const [headOrgs, setHeadOrgs] = useState([]);
    const [headDepts, setHeadDepts] = useState([]);
    const [headDesignations, setHeadDesignations] = useState([]);

    // Site Office
    const [selectedSites, setSelectedSites] = useState([]);
    const [siteOfficeSelection, setSiteOfficeSelection] = useState(null);
    const [siteOrgs, setSiteOrgs] = useState([]);
    const [siteDepts, setSiteDepts] = useState([]);
    const [siteDesignations, setSiteDesignations] = useState([]);

    // Data
    const [sites, setSites] = useState([]);
    const [org, setOrg] = useState([]);
    const [headDepartments, setHeadDepartments] = useState([]);
    const [siteDepartments, setSiteDepartments] = useState([]);
    const [allDesignations, setAllDesignations] = useState([]);

    const { authData, backUrl } = useContext(AuthContext);

    // Fetch Functions
    const fetchOrganizations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`);
            setOrg(res.data.results || []);
        } catch (err) {
            message.error("Failed to load organizations");
        }
    };

    const fetchDepartments = async (orgIds, isHead = true) => {
        try {
            const allDepartments = [];
            for (const orgId of orgIds) {
                const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`);
                allDepartments.push(...(res.data.results || []));
            }

            const uniqueDepts = allDepartments.filter((dept, index, self) =>
                index === self.findIndex((d) => d.id === dept.id)
            );

            if (isHead) setHeadDepartments(uniqueDepts);
            else setSiteDepartments(uniqueDepts);
        } catch (err) {
            message.error("Failed to load departments");
        }
    };

    const fetchDesignations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/`);
            setAllDesignations(res.data.results || []);
        } catch (err) {
            message.error("Failed to load designations");
        }
    };

    const fetchWorkLocation = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/worklocation/`);
            setSites(res.data.results || []);
        } catch (err) {
            message.error("Failed to load sites");
        }
    };

    useEffect(() => {
        fetchOrganizations();
        fetchWorkLocation();
        fetchDesignations();
    }, []);

    useEffect(() => {
        if (headOrgs.length > 0) {
            fetchDepartments(headOrgs, true);
        } else {
            setHeadDepartments([]);
        }
    }, [headOrgs]);

    useEffect(() => {
        if (siteOrgs.length > 0) {
            fetchDepartments(siteOrgs, false);
        } else {
            setSiteDepartments([]);
        }
    }, [siteOrgs]);

    const disablePastDates = (current) => current && current < dayjs().startOf("day");

    const handleSubmit = async () => {
        let finalOrgs = [];
        let finalDepts = [];
        let finalDesignations = [];
        let finalWorkLocations = [];

        // Head Office Processing
        if (officeType.includes("head")) {
            if (headOfficeSelection === "organization") {
                if (headOrgs.length === 0) {
                    return message.error("Select at least one organization for Head Office");
                }
                finalOrgs.push(...headOrgs);
            } else if (headOfficeSelection === "department") {
                if (headOrgs.length === 0 || headDepts.length === 0) {
                    return message.error("Select organization(s) and at least one department for Head Office");
                }
                finalOrgs.push(...headOrgs);
                finalDepts.push(...headDepts);
            } else if (headOfficeSelection === "designation") {
                if (headDesignations.length === 0) {
                    return message.error("Select at least one designation for Head Office");
                }
                finalDesignations.push(...headDesignations);
            }
        }

        // Site Office Processing
        if (officeType.includes("site")) {
            if (selectedSites.length === 0) return message.error("Select at least one site");
            finalWorkLocations.push(...selectedSites);

            if (siteOfficeSelection === "organization") {
                if (siteOrgs.length === 0) {
                    return message.error("Select at least one organization for site employees");
                }
                finalOrgs.push(...siteOrgs);
            } else if (siteOfficeSelection === "department") {
                if (siteOrgs.length === 0 || siteDepts.length === 0) {
                    return message.error("Select organization(s) and departments for site employees");
                }
                finalOrgs.push(...siteOrgs);
                finalDepts.push(...siteDepts);
            } else if (siteOfficeSelection === "designation") {
                if (siteDesignations.length === 0) {
                    return message.error("Select at least one designation for site employees");
                }
                finalDesignations.push(...siteDesignations);
            }
        }

        // Remove duplicates
        finalOrgs = [...new Set(finalOrgs)];
        finalDepts = [...new Set(finalDepts)];
        finalDesignations = [...new Set(finalDesignations)];
        finalWorkLocations = [...new Set(finalWorkLocations)];

        const payload = {
            date: selectedDate ? selectedDate.format("YYYY-MM-DD") : null,
            new_start_time: startTime ? startTime.format("HH:mm:ss") : null,
            new_end_time: endTime ? endTime.format("HH:mm:ss") : null,
            departments: finalDepts.length > 0 ? finalDepts : null,
            organisations: finalOrgs,
            designations: finalDesignations,
            work_locations: headOfficeSelection ? [1] : finalWorkLocations,
            gender: gender === "all" ? "" : gender,
            reason: reason.trim(),
        };

        setSubmitting(true);
        try {
            await axios.post(`${backUrl}/api/hr/permission-considerations/`, payload, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            message.success("Special permission created successfully!");

            // Reset form
            setSelectedDate(null);
            setStartTime(null);
            setEndTime(null);
            setGender(null);
            setOfficeType([]);
            setReason("");
            setHeadOfficeSelection(null);
            setHeadOrgs([]);
            setHeadDepts([]);
            setHeadDesignations([]);
            setSelectedSites([]);
            setSiteOfficeSelection(null);
            setSiteOrgs([]);
            setSiteDepts([]);
            setSiteDesignations([]);
        } catch (err) {
            message.error(err.response?.data?.message || "Failed to submit permission");
        } finally {
            setSubmitting(false);
        }
    };

    const isFormComplete = () => {
        if (!selectedDate || !startTime || !endTime || !gender || officeType.length === 0 || !reason.trim()) return false;

        if (officeType.includes("head")) {
            if (!headOfficeSelection) return false;
            if (headOfficeSelection === "organization" && headOrgs.length === 0) return false;
            if (headOfficeSelection === "department" && (headOrgs.length === 0 || headDepts.length === 0)) return false;
            if (headOfficeSelection === "designation" && headDesignations.length === 0) return false;
        }

        if (officeType.includes("site")) {
            if (selectedSites.length === 0 || !siteOfficeSelection) return false;
            if (siteOfficeSelection === "organization" && siteOrgs.length === 0) return false;
            if (siteOfficeSelection === "department" && (siteOrgs.length === 0 || siteDepts.length === 0)) return false;
            if (siteOfficeSelection === "designation" && siteDesignations.length === 0) return false;
        }

        return true;
    };

    return (
        <div style={{
            background: "#ffffff",
            minHeight: "100vh",
            padding: "20px 10px"
        }}>
            <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
                <Card
                    style={{
                        borderRadius: "16px",
                        overflow: "hidden",
                        boxShadow: "0 10px 40px rgba(14, 73, 123, 0.12)",
                        background: "white",
                        border: "none"
                    }}
                    bodyStyle={{ padding: 0 }}
                    title="MAKE SPECIAL PERMISSION"
                >
                    <div style={{ padding: "48px 32px" }}>
                        <div style={{ marginBottom: "40px" }}>
                            <Row gutter={[24, 24]}>
                                <Col xs={24} md={12}>
                                    <Text strong style={{
                                        color: "#0e497b",
                                        fontSize: "15px",
                                        display: "block",
                                        marginBottom: "8px"
                                    }}>
                                        Permission Date
                                    </Text>
                                    <DatePicker
                                        onChange={(date) => setSelectedDate(date)}
                                        value={selectedDate}
                                        style={{
                                            width: "100%",
                                            height: "30px",
                                            borderRadius: "8px",
                                            fontSize: "13px",
                                            borderColor: "#d9d9d9"
                                        }}
                                        placeholder="Select date"
                                        format="YYYY-MM-DD"
                                    />
                                </Col>
                                <Col xs={24} md={6}>
                                    <Text strong style={{
                                        color: "#0e497b",
                                        fontSize: "15px",
                                        display: "block",
                                        marginBottom: "8px"
                                    }}>
                                        Start Time
                                    </Text>
                                    <TimePicker
                                        format="HH:mm"
                                        onChange={(time) => setStartTime(time)}
                                        value={startTime}
                                        style={{
                                            width: "100%",
                                            height: "30px",
                                            borderRadius: "8px",
                                            fontSize: "13px",
                                            borderColor: "#d9d9d9"
                                        }}
                                        placeholder="Select time"
                                    />
                                </Col>

                                <Col xs={24} md={6}>
                                    <Text strong style={{
                                        color: "#0e497b",
                                        fontSize: "15px",
                                        display: "block",
                                        marginBottom: "8px"
                                    }}>
                                        End Time
                                    </Text>
                                    <TimePicker
                                        format="HH:mm"
                                        onChange={(time) => setEndTime(time)}
                                        value={endTime}
                                        style={{
                                            width: "100%",
                                            height: "30px",
                                            borderRadius: "8px",
                                            fontSize: "13px",
                                            borderColor: "#d9d9d9"
                                        }}
                                        placeholder="Select time"
                                    />
                                </Col>
                            </Row>
                        </div>

                        <section style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '20px' }}>
                            {(startTime || endTime) && (
                                <div style={{ marginBottom: "40px" }}>
                                    <Title level={4} style={{
                                        color: "#0e497b",
                                        marginBottom: "20px",
                                        fontSize: "15px",
                                        fontWeight: 600,
                                        textAlign: "center"
                                    }}>
                                        Applicable For
                                    </Title>
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                        <Radio.Group
                                            buttonStyle="solid"
                                            size="large"
                                            onChange={(e) => setGender(e.target.value)}
                                            value={gender}
                                            style={{ display: "flex" }}
                                        >
                                            <Radio.Button
                                                value="Male"
                                                style={{
                                                    minWidth: "100px",
                                                    height: "30px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: "13px",
                                                    fontWeight: 500,
                                                }}
                                            >
                                                Male
                                            </Radio.Button>
                                            <Radio.Button
                                                value="Female"
                                                style={{
                                                    minWidth: "100px",
                                                    height: "30px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: "13px",
                                                    fontWeight: 500,
                                                }}
                                            >
                                                Female
                                            </Radio.Button>
                                            <Radio.Button
                                                value="all"
                                                style={{
                                                    minWidth: "100px",
                                                    height: "30px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: "13px",
                                                    fontWeight: 500,
                                                }}
                                            >
                                                All
                                            </Radio.Button>
                                        </Radio.Group>
                                    </div>
                                </div>
                            )}

                            {gender && (
                                <div style={{ marginBottom: "40px" }}>
                                    <Title level={4} style={{
                                        color: "#0e497b",
                                        marginBottom: "20px",
                                        fontSize: "15px",
                                        fontWeight: 600,
                                        textAlign: "center"
                                    }}>
                                        Office Type
                                    </Title>
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                        <Checkbox.Group
                                            value={officeType}
                                            onChange={(v) => {
                                                setOfficeType(v);
                                                if (!v.includes("head")) {
                                                    setHeadOfficeSelection(null);
                                                    setHeadOrgs([]);
                                                    setHeadDepts([]);
                                                    setHeadDesignations([]);
                                                }
                                                if (!v.includes("site")) {
                                                    setSelectedSites([]);
                                                    setSiteOfficeSelection(null);
                                                    setSiteOrgs([]);
                                                    setSiteDepts([]);
                                                    setSiteDesignations([]);
                                                }
                                            }}
                                        >
                                            <Space size={20}>
                                                <Checkbox
                                                    value="head"
                                                    style={{
                                                        fontSize: "15px",
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    <Text strong style={{ color: "#0e497b", fontSize: "13px" }}>
                                                        Head Office
                                                    </Text>
                                                </Checkbox>
                                                <Checkbox
                                                    value="site"
                                                    style={{
                                                        fontSize: "15px",
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    <Text strong style={{ color: "#0e497b", fontSize: "13px" }}>
                                                        Site Office
                                                    </Text>
                                                </Checkbox>
                                            </Space>
                                        </Checkbox.Group>
                                    </div>
                                </div>
                            )}
                        </section>

                        {/* Office Cards */}
                        {(officeType.includes("head") || officeType.includes("site")) && (
                            <Row gutter={[24, 24]} style={{ marginBottom: "40px" }}>
                                {officeType.includes("head") && (
                                    <Col xs={24} lg={12}>
                                        <Card
                                            style={{
                                                borderRadius: "12px",
                                                boxShadow: "0 4px 16px rgba(14, 73, 123, 0.08)",
                                                border: "1px solid #e8eef5",
                                                height: "100%"
                                            }}
                                            headStyle={{
                                                background: "#ffffff",
                                                borderBottom: "1px solid #e8eef5",
                                                borderRadius: "12px 12px 0 0",
                                                padding: "16px 24px"
                                            }}
                                            bodyStyle={{ padding: "24px" }}
                                            title={
                                                <Text strong style={{
                                                    color: "#0e497b",
                                                    fontSize: "15px",
                                                    fontWeight: 600
                                                }}>
                                                    Head Office Configuration
                                                </Text>
                                            }
                                        >
                                            <Radio.Group
                                                onChange={(e) => {
                                                    setHeadOfficeSelection(e.target.value);
                                                    if (e.target.value === "all") {
                                                        setHeadOrgs([]);
                                                        setHeadDepts([]);
                                                        setHeadDesignations([]);
                                                    } else if (e.target.value === "organization") {
                                                        setHeadDepts([]);
                                                        setHeadDesignations([]);
                                                    } else if (e.target.value === "department") {
                                                        setHeadDesignations([]);
                                                    } else if (e.target.value === "designation") {
                                                        setHeadOrgs([]);
                                                        setHeadDepts([]);
                                                    }
                                                }}
                                                value={headOfficeSelection}
                                                style={{ width: "100%" }}
                                            >
                                                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                                                    <Radio value="all" style={{ fontSize: "15px" }}>
                                                        All Employees
                                                    </Radio>
                                                    <Radio value="organization" style={{ fontSize: "15px" }}>
                                                        All Employees in Organization(s)
                                                    </Radio>
                                                    <Radio value="department" style={{ fontSize: "15px" }}>
                                                        Specific Department(s)
                                                    </Radio>
                                                    {/* <Radio value="designation" style={{ fontSize: "15px" }}>
                                                        Specific Designation(s)
                                                    </Radio> */}
                                                </Space>
                                            </Radio.Group>

                                            {headOfficeSelection === "designation" && (
                                                <div style={{ marginTop: "24px" }}>
                                                    <Text strong style={{
                                                        color: "#0e497b",
                                                        fontSize: "15px",
                                                        display: "block",
                                                        marginBottom: "8px"
                                                    }}>
                                                        Designation(s) *
                                                    </Text>
                                                    <Select
                                                        mode="multiple"
                                                        placeholder="Choose designations"
                                                        style={{ width: "100%" }}
                                                        value={headDesignations}
                                                        onChange={setHeadDesignations}
                                                        size="medium"
                                                        allowClear
                                                        showSearch
                                                        optionFilterProp="children"
                                                    >
                                                        {allDesignations.map((d) => (
                                                            <Option key={d.id} value={d.id}>{d.name}</Option>
                                                        ))}
                                                    </Select>
                                                </div>
                                            )}

                                            {(headOfficeSelection === "organization" || headOfficeSelection === "department") && (
                                                <div style={{ marginTop: "24px" }}>
                                                    <Text strong style={{
                                                        color: "#0e497b",
                                                        fontSize: "15px",
                                                        display: "block",
                                                        marginBottom: "8px"
                                                    }}>
                                                        Organization(s) *
                                                    </Text>
                                                    <Select
                                                        mode="multiple"
                                                        placeholder="Choose organizations"
                                                        style={{ width: "100%" }}
                                                        value={headOrgs}
                                                        onChange={(val) => {
                                                            setHeadOrgs(val);
                                                            if (headOfficeSelection === "department") {
                                                                setHeadDepts([]);
                                                            }
                                                        }}
                                                        size="medium"
                                                        allowClear
                                                        showSearch
                                                        optionFilterProp="children"
                                                    >
                                                        {org.map((o) => (
                                                            <Option key={o.id} value={o.id}>{o.name}</Option>
                                                        ))}
                                                    </Select>

                                                    {headOfficeSelection === "department" && headOrgs.length > 0 && (
                                                        <div style={{ marginTop: "20px" }}>
                                                            <Text strong style={{
                                                                color: "#0e497b",
                                                                fontSize: "15px",
                                                                display: "block",
                                                                marginBottom: "8px"
                                                            }}>
                                                                Department(s) *
                                                            </Text>
                                                            <Select
                                                                mode="multiple"
                                                                placeholder="Select departments"
                                                                style={{ width: "100%" }}
                                                                value={headDepts}
                                                                onChange={setHeadDepts}
                                                                allowClear
                                                                showSearch
                                                                optionFilterProp="children"
                                                                size="medium"
                                                            >
                                                                {headDepartments.map((d) => (
                                                                    <Option key={d.id} value={d.id}>
                                                                        {d.name}
                                                                    </Option>
                                                                ))}
                                                            </Select>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </Card>
                                    </Col>
                                )}

                                {officeType.includes("site") && (
                                    <Col xs={24} lg={12}>
                                        <Card
                                            style={{
                                                borderRadius: "12px",
                                                boxShadow: "0 4px 16px rgba(14, 73, 123, 0.08)",
                                                border: "1px solid #e8eef5",
                                                height: "100%"
                                            }}
                                            headStyle={{
                                                background: "#ffffff",
                                                borderBottom: "1px solid #e8eef5",
                                                borderRadius: "12px 12px 0 0",
                                                padding: "16px 24px"
                                            }}
                                            bodyStyle={{ padding: "24px" }}
                                            title={
                                                <Text strong style={{
                                                    color: "#0e497b",
                                                    fontSize: "15px",
                                                    fontWeight: 600
                                                }}>
                                                    Site Office Configuration
                                                </Text>
                                            }
                                        >
                                            <Text strong style={{
                                                color: "#0e497b",
                                                fontSize: "15px",
                                                display: "block",
                                                marginBottom: "12px"
                                            }}>
                                                Select Site(s) *
                                            </Text>
                                            <div style={{
                                                maxHeight: "200px",
                                                overflowY: "auto",
                                                padding: "12px",
                                                background: "#ffffffff",
                                                borderRadius: "8px",
                                                border: "1px solid #e8eef5"
                                            }}>
                                                <Checkbox.Group value={selectedSites} onChange={setSelectedSites}>
                                                    <Space direction="vertical" style={{ width: "100%" }}>
                                                        {sites.map((s) => (
                                                            <Checkbox key={s.id} value={s.id} style={{ fontSize: "13px" }}>
                                                                {s.name}
                                                            </Checkbox>
                                                        ))}
                                                    </Space>
                                                </Checkbox.Group>
                                            </div>

                                            {selectedSites.length > 0 && (
                                                <>
                                                    <Divider style={{ margin: "24px 0", borderColor: "#e8eef5" }} />
                                                    <Radio.Group
                                                        onChange={(e) => {
                                                            setSiteOfficeSelection(e.target.value);
                                                            if (e.target.value === "organization") {
                                                                setSiteDepts([]);
                                                                setSiteDesignations([]);
                                                            } else if (e.target.value === "department") {
                                                                setSiteDesignations([]);
                                                            } else if (e.target.value === "designation") {
                                                                setSiteOrgs([]);
                                                                setSiteDepts([]);
                                                            }
                                                        }}
                                                        value={siteOfficeSelection}
                                                        style={{ width: "100%" }}
                                                    >
                                                        <Space direction="vertical" size={16} style={{ width: "100%" }}>
                                                            <Radio value="organization" style={{ fontSize: "15px" }}>
                                                                All Employees in Organization(s)
                                                            </Radio>
                                                            <Radio value="department" style={{ fontSize: "15px" }}>
                                                                Specific Department(s)
                                                            </Radio>
                                                            {/* <Radio value="designation" style={{ fontSize: "15px" }}>
                                                                Specific Designation(s)
                                                            </Radio> */}
                                                        </Space>
                                                    </Radio.Group>

                                                    {siteOfficeSelection === "designation" && (
                                                        <div style={{ marginTop: "24px" }}>
                                                            <Text strong style={{
                                                                color: "#0e497b",
                                                                fontSize: "15px",
                                                                display: "block",
                                                                marginBottom: "8px"
                                                            }}>
                                                                Designation(s) *
                                                            </Text>
                                                            <Select
                                                                mode="multiple"
                                                                placeholder="Choose designations"
                                                                style={{ width: "100%" }}
                                                                value={siteDesignations}
                                                                onChange={setSiteDesignations}
                                                                size="medium"
                                                                allowClear
                                                                showSearch
                                                                optionFilterProp="children"
                                                            >
                                                                {allDesignations.map((d) => (
                                                                    <Option key={d.id} value={d.id}>{d.name}</Option>
                                                                ))}
                                                            </Select>
                                                        </div>
                                                    )}

                                                    {(siteOfficeSelection === "organization" || siteOfficeSelection === "department") && (
                                                        <div style={{ marginTop: "24px" }}>
                                                            <Text strong style={{
                                                                color: "#0e497b",
                                                                fontSize: "15px",
                                                                display: "block",
                                                                marginBottom: "8px"
                                                            }}>
                                                                Organization(s) *
                                                            </Text>
                                                            <Select
                                                                mode="multiple"
                                                                placeholder="Choose organizations"
                                                                style={{ width: "100%" }}
                                                                value={siteOrgs}
                                                                onChange={(val) => {
                                                                    setSiteOrgs(val);
                                                                    if (siteOfficeSelection === "department") {
                                                                        setSiteDepts([]);
                                                                    }
                                                                }}
                                                                size="medium"
                                                                allowClear
                                                                showSearch
                                                                optionFilterProp="children"
                                                            >
                                                                {org.map((o) => (
                                                                    <Option key={o.id} value={o.id}>{o.name}</Option>
                                                                ))}
                                                            </Select>

                                                            {siteOfficeSelection === "department" && siteOrgs.length > 0 && (
                                                                <div style={{ marginTop: "20px" }}>
                                                                    <Text strong style={{
                                                                        color: "#0e497b",
                                                                        fontSize: "15px",
                                                                        display: "block",
                                                                        marginBottom: "8px"
                                                                    }}>
                                                                        Department(s) *
                                                                    </Text>
                                                                    <Select
                                                                        mode="multiple"
                                                                        placeholder="Select departments"
                                                                        style={{ width: "100%" }}
                                                                        value={siteDepts}
                                                                        onChange={setSiteDepts}
                                                                        allowClear
                                                                        showSearch
                                                                        size="medium"
                                                                    >
                                                                        {siteDepartments.map((d) => (
                                                                            <Option key={d.id} value={d.id}>
                                                                                {d.name}
                                                                            </Option>
                                                                        ))}
                                                                    </Select>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </Card>
                                    </Col>
                                )}
                            </Row>
                        )}

                        {/* Reason Section */}
                        {(officeType.includes("head") || officeType.includes("site")) && (
                            <>
                                <Divider style={{ borderColor: "#f3f1f1ff", margin: "40px 0" }} />
                                <div style={{ marginBottom: "40px" }}>
                                    <Title level={4} style={{
                                        color: "#0e497b",
                                        marginBottom: "12px",
                                        fontSize: "15px",
                                        fontWeight: 600
                                    }}>
                                        Reason for Special Permission
                                    </Title>
                                    <TextArea
                                        rows={5}
                                        placeholder="Please describe the reason for this special permission"
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        style={{
                                            borderRadius: "8px",
                                            fontSize: "13px",
                                            borderColor: "#d9d9d9",
                                            resize: "none"
                                        }}
                                        maxLength={500}
                                        showCount
                                    />
                                </div>

                                {/* Submit Button */}
                                <Row justify="center">
                                    <Button
                                        type="primary"
                                        size="large"
                                        onClick={handleSubmit}
                                        loading={submitting}
                                        // disabled={submitting || !isFormComplete()}
                                        style={{
                                            background: "#0e497b",
                                            borderColor: "#0e497b",
                                            transition: "all 0.3s ease"
                                        }}
                                    >
                                        {submitting ? "Submitting..." : "Submit Special Permission"}
                                    </Button>
                                </Row>
                            </>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
}